import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../../services/api';

const {
    FiSettings, FiSave, FiType,
    FiPalette, FiGlobe, FiCode, FiFacebook, FiTwitter,
    FiLinkedin, FiInstagram, FiYoutube, FiGithub, FiImage,
    FiClock
} = FiIcons;

const BrandingPage = () => {
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(true);
    const [brandingData, setBrandingData] = useState({
        applicationName: 'Softvative Expense Tracker',
        applicationTitle: 'Professional Expense Management',
        primaryColor: '#3b82f6',
        accentColor: '#d946ef',
        fontFamily: 'Inter',
        fontSize: '16',
        timezone: 'America/Chicago',
        facebookUrl: '',
        twitterUrl: '',
        linkedinUrl: '',
        instagramUrl: '',
        youtubeUrl: '',
        githubUrl: '',
        customCss: '',
        customJs: ''
    });

    useEffect(() => {
        loadBranding();
    }, []);

    const loadBranding = async () => {
        try {
            const data = await api.get('branding');
            if (data) setBrandingData(prev => ({ ...prev, ...data.data.data }));
        } catch (error) {
            toast.error('Failed to load branding settings');
        } finally {
            setLoading(false);
        }
    };

     const handleSave = async () => {
        try {
            await api.put('branding', brandingData);
            toast.success('Branding settings saved successfully!');
            window.dispatchEvent(new Event('storage'));
        } catch (error) {
            toast.error('Failed to save settings');
        }
    };

    const tabs = [
        { id: 'general', label: 'General', icon: FiSettings },
        { id: 'social', label: 'Social Links', icon: FiGlobe },
        // { id: 'code', label: 'Custom Code', icon: FiCode }
    ];

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Branding</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">Customize your application's identity and look</p>
                    </div>
                    <button
                        onClick={handleSave}
                        className="flex items-center justify-center px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg font-medium"
                    >
                        <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
                        Save Changes
                    </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Tabs */}
                    <div className="lg:w-64 flex-shrink-0">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center px-6 py-4 text-sm font-medium transition-colors border-l-4 ${activeTab === tab.id
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-primary-600'
                                            : 'text-gray-600 dark:text-gray-400 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                >
                                    <SafeIcon icon={tab.icon} className="w-5 h-5 mr-3" />
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                            {activeTab === 'general' && (
                                <div className="space-y-10">
                                    {/* Basic Information */}
                                    <div className="space-y-6">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2 flex items-center">
                                            <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2 text-primary-500" />
                                            General Settings
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Application Name</label>
                                                <input
                                                    type="text"
                                                    value={brandingData.applicationName}
                                                    onChange={(e) => setBrandingData({ ...brandingData, applicationName: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Application Title</label>
                                                <input
                                                    type="text"
                                                    value={brandingData.applicationTitle}
                                                    onChange={(e) => setBrandingData({ ...brandingData, applicationTitle: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                                                    <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                                                    Default Timezone
                                                </label>
                                                <select
                                                    value={brandingData.timezone}
                                                    onChange={(e) => setBrandingData({ ...brandingData, timezone: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                                >
                                                    <option value="UTC">UTC</option>
                                                    <option value="America/New_York">Eastern Time (US & Canada)</option>
                                                    <option value="America/Chicago">Central Time (US & Canada)</option>
                                                    <option value="America/Denver">Mountain Time (US & Canada)</option>
                                                    <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
                                                    <option value="Europe/London">London</option>
                                                    <option value="Europe/Paris">Paris</option>
                                                    <option value="Asia/Tokyo">Tokyo</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Appearance Settings */}
                                    <div className="space-y-6">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2 flex items-center">
                                            <SafeIcon icon={FiPalette} className="w-5 h-5 mr-2 text-primary-500" />
                                            Appearance
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div className="space-y-4">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">Primary Color</label>
                                                <div className="flex gap-4">
                                                    <input
                                                        type="color"
                                                        value={brandingData.primaryColor}
                                                        onChange={(e) => setBrandingData({ ...brandingData, primaryColor: e.target.value })}
                                                        className="h-10 w-10 rounded cursor-pointer border border-gray-200 dark:border-gray-600 bg-transparent"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={brandingData.primaryColor}
                                                        onChange={(e) => setBrandingData({ ...brandingData, primaryColor: e.target.value })}
                                                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-4">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">Accent Color</label>
                                                <div className="flex gap-4">
                                                    <input
                                                        type="color"
                                                        value={brandingData.accentColor}
                                                        onChange={(e) => setBrandingData({ ...brandingData, accentColor: e.target.value })}
                                                        className="h-10 w-10 rounded cursor-pointer border border-gray-200 dark:border-gray-600 bg-transparent"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={brandingData.accentColor}
                                                        onChange={(e) => setBrandingData({ ...brandingData, accentColor: e.target.value })}
                                                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Typography Settings */}
                                    <div className="space-y-6">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2 flex items-center">
                                            <SafeIcon icon={FiType} className="w-5 h-5 mr-2 text-primary-500" />
                                            Typography
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Font Family</label>
                                                <select
                                                    value={brandingData.fontFamily}
                                                    onChange={(e) => setBrandingData({ ...brandingData, fontFamily: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                                >
                                                    <option value="Inter">Inter</option>
                                                    <option value="Roboto">Roboto</option>
                                                    <option value="Open Sans">Open Sans</option>
                                                    <option value="Lato">Lato</option>
                                                    <option value="Montserrat">Montserrat</option>
                                                    <option value="Poppins">Poppins</option>
                                                    <option value="System UI">System UI</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Base Font Size (px)</label>
                                                <input
                                                    type="number"
                                                    min="12"
                                                    max="24"
                                                    value={brandingData.fontSize}
                                                    onChange={(e) => setBrandingData({ ...brandingData, fontSize: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                                />
                                            </div>
                                        </div>

                                        <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Typography Preview</p>
                                            <div style={{ fontFamily: brandingData.fontFamily, fontSize: `${brandingData.fontSize}px` }}>
                                                <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">The quick brown fox jumps over the lazy dog.</h4>
                                                <p className="text-gray-600 dark:text-gray-400">
                                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sized at {brandingData.fontSize}px using the {brandingData.fontFamily} font family.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'social' && (
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-4">Social Media Profiles</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                                <SafeIcon icon={FiFacebook} className="w-4 h-4 mr-2 text-blue-600" />
                                                Facebook URL
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://facebook.com/..."
                                                value={brandingData.facebookUrl}
                                                onChange={(e) => setBrandingData({ ...brandingData, facebookUrl: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                                <SafeIcon icon={FiTwitter} className="w-4 h-4 mr-2 text-sky-500" />
                                                Twitter URL
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://twitter.com/..."
                                                value={brandingData.twitterUrl}
                                                onChange={(e) => setBrandingData({ ...brandingData, twitterUrl: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                                <SafeIcon icon={FiLinkedin} className="w-4 h-4 mr-2 text-blue-700" />
                                                LinkedIn URL
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://linkedin.com/company/..."
                                                value={brandingData.linkedinUrl}
                                                onChange={(e) => setBrandingData({ ...brandingData, linkedinUrl: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                                <SafeIcon icon={FiInstagram} className="w-4 h-4 mr-2 text-pink-600" />
                                                Instagram URL
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://instagram.com/..."
                                                value={brandingData.instagramUrl}
                                                onChange={(e) => setBrandingData({ ...brandingData, instagramUrl: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                                <SafeIcon icon={FiYoutube} className="w-4 h-4 mr-2 text-red-600" />
                                                YouTube URL
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://youtube.com/..."
                                                value={brandingData.youtubeUrl}
                                                onChange={(e) => setBrandingData({ ...brandingData, youtubeUrl: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-4">
                                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                                <SafeIcon icon={FiGithub} className="w-4 h-4 mr-2 text-gray-900 dark:text-white" />
                                                GitHub URL
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://github.com/..."
                                                value={brandingData.githubUrl}
                                                onChange={(e) => setBrandingData({ ...brandingData, githubUrl: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'code' && (
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-4">Custom Integrations</h3>
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom CSS</label>
                                            <textarea
                                                rows={6}
                                                value={brandingData.customCss}
                                                onChange={(e) => setBrandingData({ ...brandingData, customCss: e.target.value })}
                                                placeholder="/* Add your custom styles here */"
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom JavaScript</label>
                                            <textarea
                                                rows={6}
                                                value={brandingData.customJs}
                                                onChange={(e) => setBrandingData({ ...brandingData, customJs: e.target.value })}
                                                placeholder="// Add your custom scripts here"
                                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrandingPage;